import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useEffect, /* useMemo, */ useState } from 'react';

import { UpsellName, UpsellPriorities, UpsellStatus, UpsellType } from '../common';
import { UpsellAnalyticsRecord, getUpsellAnalytics } from '../services/Market.service';

interface UpsellAnalyticsSummary {
  hasRecentInteraction: boolean;
  totalUpsellsShown: number;
  shownToday: boolean;
}

export const useUpsellSelection = (
  cells: string[],
  canShowKADUpsell: boolean
): { shouldShowRDDUpsell: boolean; shouldShowRetailKADUpsell: boolean } => {
  const { getAccessToken } = useAuthentication();
  const [rddUpsellAnalyticsSummary, setRddUpsellAnalyticsSummary] = useState<UpsellAnalyticsSummary>({
    hasRecentInteraction: false,
    totalUpsellsShown: 0,
    shownToday: false,
  });
  const [retailKADUpsellAnalyticsSummary, setRetailKADUpsellAnalyticsSummary] = useState<UpsellAnalyticsSummary>({
    hasRecentInteraction: false,
    totalUpsellsShown: 0,
    shownToday: false,
  });

  const [shouldShowRDDUpsell, setShouldShowRDDUpsell] = useState<boolean>(false);
  const [shouldShowRetailKADUpsell, setShouldShowRetailKADUpsell] = useState<boolean>(false);

  /**
   * Analyzes an array of upsell analytics records to generate a summary of user interactions.
   *
   * @param analyticsData - Array of upsell analytics records containing timestamp and status information
   * @returns {UpsellAnalyticsSummary} A summary object containing:
   *   - hasRecentInteraction: Whether user interacted (accepted/declined) with upsells in last 2 months
   *   - totalUpsellsShown: Total number of upsells shown in last 2 months
   *   - shownToday: Whether an upsell was shown today
   *
   * @example
   * const summary = checkAnalyticsData(analyticsRecords);
   * if (!summary.shownToday && !summary.hasRecentInteraction) {
   *   // Safe to show new upsell
   * }
   */
  const checkAnalyticsData = (analyticsData: UpsellAnalyticsRecord[]): UpsellAnalyticsSummary => {
    const defaultUpsellSummary: UpsellAnalyticsSummary = {
      hasRecentInteraction: false,
      totalUpsellsShown: 0,
      shownToday: false,
    };
    if (!Array.isArray(analyticsData)) {
      return defaultUpsellSummary;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const cutoffTime = twoMonthsAgo.getTime();

    return analyticsData.reduce((summary, data) => {
      if (!data?.timeStamp) {
        return summary;
      }

      const analyticsTime = new Date(data.timeStamp).getTime();
      const isWithinTwoMonths = analyticsTime >= cutoffTime;
      const isToday = analyticsTime >= today;
      const type = data.upsellStatus?.toLowerCase();

      if (isWithinTwoMonths) {
        // Check for interactions (accept/reject)
        if (type === UpsellStatus.UPSELL_ACTIVATED || type === UpsellStatus.UPSELL_DECLINED) {
          summary.hasRecentInteraction = true;
        }

        // Count total shown
        if (type === UpsellStatus.UPSELL_SHOWN) {
          summary.totalUpsellsShown += 1;
        }
      }

      // Check if shown today
      if (isToday && type === UpsellStatus.UPSELL_SHOWN) {
        summary.shownToday = true;
      }

      return summary;
    }, defaultUpsellSummary);
  };

  const shouldShowUpsell = (analytics: UpsellAnalyticsSummary): boolean =>
    !analytics.hasRecentInteraction && analytics.totalUpsellsShown < 2 && !analytics.shownToday;

  useEffect(() => {
    (async () => {
      const token = await getAccessToken();
      const [rddUpsellAnalytics, retailKADUpsellAnalytics] = await Promise.all([
        UpsellPriorities.get(UpsellType.MONTHLY_INSIGHTS_UPSELL) &&
          getUpsellAnalytics(token, { upsellName: UpsellName.MONTHLY_INSIGHTS }),
        canShowKADUpsell && getUpsellAnalytics(token, { upsellName: UpsellName.RETAIL_KAD }),
      ]);
      setRddUpsellAnalyticsSummary(checkAnalyticsData(rddUpsellAnalytics));
      setRetailKADUpsellAnalyticsSummary(checkAnalyticsData(retailKADUpsellAnalytics));
    })();
  }, [getAccessToken, cells, canShowKADUpsell]);

  useEffect(() => {
    setShouldShowRDDUpsell(shouldShowUpsell(rddUpsellAnalyticsSummary));
    setShouldShowRetailKADUpsell(shouldShowUpsell(retailKADUpsellAnalyticsSummary) && canShowKADUpsell);
  }, [rddUpsellAnalyticsSummary, canShowKADUpsell, retailKADUpsellAnalyticsSummary]);

  return {
    shouldShowRDDUpsell,
    shouldShowRetailKADUpsell,
  };
};
